import React, { useState, useEffect, useRef } from 'react';
import { Text, View, Keyboard, Platform } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import Input from '@/components/General/inputs/Input';
import { Query } from '@/utils/react-query/Queries'
import { QueryKeys } from '@/utils/react-query/QueryKeys'
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { savePost } from '@/data/connect';
import Avatar from '@/components/People/Avatar';
import { removeStorageFile } from '@/utils/FileManager';
import { parseMentionSegments } from '@/utils/mentionFormat';
import * as Validator from '@/utils/InputValidator';
import Toast from 'react-native-toast-message'
import { useRouter, useLocalSearchParams } from 'expo-router';
import { useEditScreenStore } from '@/data/store/useEditScreenStore';
import ModalWrapper from '@/components/General/ModalWrapper';
import MessageInput from '@/components/Connect/Conversations/MessageInput';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { ScheduleDatePicker, ScheduleModeMenu, getScheduledDate } from '@/components/General/SchedulePicker';

const EMPTY_DRAFT = {};

export default function AddPostModal() {
    const { id, boardID, boardName } = useLocalSearchParams()
    const storeKey = id || "new"

    const setEditScreenData = useEditScreenStore(s => s.setEditScreenData)
    const removeEditScreenData = useEditScreenStore(s => s.removeEditScreenData)
    const postDetails = useEditScreenStore(s => s.AddPostModal?.[storeKey]) || EMPTY_DRAFT

    /*** State ***/
    const [saving, setSaving] = useState(false);
    const messageInputRef = useRef(null);

    /*** Theme & Dimensions ***/
    const router = useRouter();

    const { data: postToEdit } = Query.posts.byId({id, options: {enabled: !!id}})
    const { data: board } = Query.boards.byId({id: boardID, options: {enabled: !!boardID && !id}})


    useEffect(() => {
        if(postToEdit){
            // Pre-select Post Later when editing a scheduled (unpublished) post,
            // pre-fill the picker with the existing postedAt.
            const isScheduled = postToEdit.isPublished === false;
            setPostDetails({
                ...postToEdit,
                scheduleMode: isScheduled ? "later" : "now",
                scheduledFor: isScheduled ? postToEdit.postedAt : null
            });

            // Decode mention tokens and pre-fill the message input.
            const segments = parseMentionSegments(postToEdit.body || '');
            const decodedBody = segments.map(s => s.text ?? s.mention.label).join('');
            const mentions = segments.filter(s => s.mention).map(s => s.mention);
            messageInputRef.current?.setMessage?.(decodedBody);
            messageInputRef.current?.setMentions?.(mentions);
        }
    }, [postToEdit]);

    const setPostDetails = (details) => {
        setEditScreenData("AddPostModal", storeKey, details)
    }

    const scheduleMode = postDetails.scheduleMode || "now";
    const isLater = scheduleMode === "later";
    const updateSchedule = (schedule) => setPostDetails({ ...postDetails, ...schedule });


    /*** Queries ***/
    const queryClient = useQueryClient()
    const { data: loggedInUser } = Query.people.loggedInUser()

    const savePostMutation = useMutation({
        mutationFn: savePost,
        onSuccess: async () => {
            queryClient.invalidateQueries({queryKey: QueryKeys.boards.byId(boardID)});
            queryClient.invalidateQueries({queryKey: QueryKeys.posts.byBoardId(boardID)});
            queryClient.invalidateQueries({queryKey: QueryKeys.posts.myBoardsFeed()});
            removeEditScreenData("AddPostModal", storeKey)
            router.back();
        }
    })


    const handlePost = async (bodyText, attachmentKeys = []) => {
        if(!Validator.areStringsPopulated([postDetails.title])){
            Toast.show({type: 'error', text2: "Your post has to have a title!"})
            return;
        }

        if(!Validator.areStringsPopulated([bodyText]) && attachmentKeys.length === 0){
            Toast.show({type: 'error', text2: "Your post has to have content!"})
            return;
        }

        setSaving(true)

        //Handle existing attachments when editing (remove ones no longer present)
        const existingAttachments = [...(postDetails.attachments || [])];
        if(existingAttachments.length > 0){
            for(let i=0; i<existingAttachments.length; i++){
                const wasRemoved = !attachmentKeys.includes(existingAttachments[i]);
                if(wasRemoved){
                    await removeStorageFile(existingAttachments[i]);
                    existingAttachments.splice(i, 1);
                }
            }
        }

        //Merge existing (kept) attachments with new ones
        const allAttachments = [...existingAttachments, ...attachmentKeys];

        const scheduledFor = getScheduledDate(scheduleMode, postDetails.scheduledFor);
        const isPublished = !scheduledFor;

        savePostMutation.mutate({
            details: {
                ...postDetails,
                title: postDetails.title || "",
                body: bodyText,
                attachments: allAttachments,
                scheduledFor,
                isPublished
            },
            boardID,
            authorID: loggedInUser?.id
        })
    }


    const confirmText = id ? "Update" : (isLater ? "Schedule" : "Post");


    if(board?.type === 'ANNOUNCEMENTS' || postToEdit?.Board?.type === 'ANNOUNCEMENTS'){
        return <ModalWrapper title="Organization announcement"><View style={styles.archivedContainer}><Text style={styles.archivedTitle}>Use the announcement composer</Text><Text style={styles.archivedSubtext}>Organization announcements require a delivery review and cannot be edited after sending.</Text></View></ModalWrapper>;
    }
    //Archived boards are read-dead — no new posts (RLS also blocks server-side).
    if(board?.isArchived){
        return (
            <ModalWrapper title="New Post">
                <View style={styles.archivedContainer} testID="create-post-archived-notice">
                    <Text style={styles.archivedTitle}>This board has been archived</Text>
                    <Text style={styles.archivedSubtext}>New posts, comments, and reactions are turned off.</Text>
                </View>
            </ModalWrapper>
        );
    }

    return (
        <ModalWrapper
            loading={saving || savePostMutation.isPending}
            confirmText={confirmText}
            onConfirm={() => {
                if(!Validator.areStringsPopulated([postDetails.title])){
                    Toast.show({type: 'error', text2: "Your post has to have a title!"})
                    return false;
                }
                const messageText = messageInputRef.current?.getMessage?.() || '';
                const messageAttachments = messageInputRef.current?.getAttachments?.() || [];
                if(!Validator.areStringsPopulated([messageText]) && messageAttachments.length === 0){
                    Toast.show({type: 'error', text2: "Your post has to have content!"})
                    return false;
                }
                messageInputRef.current?.sendMessage?.();
            }} title={(id ? "Update " : "New ") + "Post"}
        >

            <KeyboardAwareScrollView bottomOffset={100} keyboardShouldPersistTaps="handled">
            <View style={styles.contentContainer}>
                <View style={styles.headerRow}>
                    <Avatar disableBorder borderGap profile={loggedInUser} size={50}/>
                    <Text style={styles.boardNameText}>{boardName}</Text>
                    <View style={styles.scheduleDropdownWrap}>
                        <ScheduleModeMenu
                            testID="post-schedule"
                            scheduleMode={scheduleMode}
                            scheduledFor={postDetails.scheduledFor}
                            onScheduleChange={updateSchedule}
                            nowLabel="Post Now"
                            laterLabel="Post Later"
                            colorVariant="connect"
                        />
                    </View>
                </View>

                {isLater && (
                    <ScheduleDatePicker
                        testID="post-schedule"
                        scheduledFor={postDetails.scheduledFor}
                        onScheduleChange={updateSchedule}
                    />
                )}

                <View style={styles.titleRow}>
                    <View style={styles.titleInputContainer}>
                        <Input
                            title="Post Title"
                            value={postDetails.title}
                            maxLength={120}
                            onUpdate={(new_value) => {
                                setPostDetails({
                                    ...postDetails,
                                    title: new_value
                                })
                            }}
                        />
                    </View>
                </View>

                <View style={styles.bodyContainer}>
                    <MessageInput
                        ref={messageInputRef}
                        placeholder="What would you like to say?"
                        detection={["mention"]}
                        enableAttachments
                        embedded
                        fixedMentionPicker={Platform.OS === 'web'}
                        hideSendButton
                        attachmentDirectory={"boardAttachments/" + boardID}
                        bottomInset={20}
                        onSendMessage={(text, attachments) => {
                            Keyboard.dismiss();
                            handlePost(text, attachments);
                        }}
                    />
                </View>
            </View>
            </KeyboardAwareScrollView>
        </ModalWrapper>
    );
}

const styles = StyleSheet.create(({colors, fonts}) => ({
    headerRow: {
        paddingVertical: 10,
        flexDirection:"row",
        alignItems:"center",
        gap:10
    },
    boardNameText: {
        color:colors.textSecondary,
        fontFamily:fonts.heading,
        fontSize:16,
        flexShrink: 1,
    },
    scheduleDropdownWrap: {
        marginLeft: "auto",
    },
    titleRow: {
        flexDirection:"row",
        alignItems:"center"
    },
    titleInputContainer: {
        flex:1
    },
    bodyContainer: {
        marginTop: 10,
    },
    contentContainer: {
        width: "100%",
        maxWidth: 700,
        alignSelf: "center",
    },
    archivedContainer: {
        alignItems: "center",
        justifyContent: "center",
        padding: 30,
    },
    archivedTitle: {
        fontSize: 20,
        fontFamily: fonts.bold,
        color: colors.text,
    },
    archivedSubtext: {
        fontSize: 14,
        color: colors.textSecondary,
        marginTop: 8,
        textAlign: "center",
    },
}));
